// TESSERAX — i18n maison, légère, sans dépendance.
// FR = langue par défaut : les libellés FR vivent dans le HTML (data-i18n) et dans
// les modules cœur (achievements/relics/missions). EN = dictionnaire ci-dessous.
// t(key, params, fallback) : EN[key] sinon fallback (texte FR d'origine) sinon FR[key] sinon key.
import { fr } from './fr';
import { en } from './en';

type Dict = Record<string, string>;
const DICTS: Record<string, Dict> = { fr, en };
export type Lang = 'fr' | 'en';
let lang: Lang = 'fr';
const listeners: Array<() => void> = [];

export function detectLang(): Lang {
  try { const s = localStorage.getItem('tesserax:lang'); if (s === 'fr' || s === 'en') return s; } catch (e) {}
  const n = (navigator.language || 'fr').toLowerCase();
  return n.startsWith('fr') ? 'fr' : 'en';
}
export function getLang(): Lang { return lang; }
export function onLangChange(f: () => void) { listeners.push(f); }

export function t(key: string, params?: Record<string, string | number>, fallback?: string): string {
  const d = DICTS[lang];
  let s: string;
  if (d && d[key] !== undefined) s = d[key];
  else if (fallback !== undefined) s = fallback;
  else if (fr[key] !== undefined) s = fr[key];
  else s = key;
  // ⚠ SÉCURITÉ : les params ne sont PAS échappés HTML (comportement voulu — la plupart
  // des résultats de t() alimentent textContent, où un échappement corromprait le texte).
  // Toute clé dont les params peuvent porter une chaîne contrôlée par l'utilisateur ou le
  // serveur (pseudo, nom de relique…) ne doit JAMAIS être routée vers innerHTML :
  // utiliser textContent, ou échapper au point d'appel (escapeHtml de core/util).
  if (params) for (const k in params) s = s.replace(new RegExp('\\{' + k + '\\}', 'g'), String(params[k]));
  return s;
}

// Applique les libellés sur le DOM statique. Au premier passage on mémorise le
// texte FR d'origine (data-i18n-def) → sert de repli pour la locale FR.
export function applyI18n(root: ParentNode = document) {
  root.querySelectorAll<HTMLElement>('[data-i18n]').forEach(el => {
    const k = el.getAttribute('data-i18n')!;
    if (el.dataset.i18nDef === undefined) el.dataset.i18nDef = el.textContent || '';
    el.textContent = t(k, undefined, el.dataset.i18nDef);
  });
  root.querySelectorAll<HTMLElement>('[data-i18n-ph]').forEach(el => {
    const k = el.getAttribute('data-i18n-ph')!;
    if (el.dataset.i18nPhDef === undefined) el.dataset.i18nPhDef = (el as HTMLInputElement).placeholder || '';
    (el as HTMLInputElement).placeholder = t(k, undefined, el.dataset.i18nPhDef);
  });
  root.querySelectorAll<HTMLElement>('[data-i18n-html]').forEach(el => {
    const k = el.getAttribute('data-i18n-html')!;
    if (el.dataset.i18nHtmlDef === undefined) el.dataset.i18nHtmlDef = el.innerHTML;
    el.innerHTML = t(k, undefined, el.dataset.i18nHtmlDef);
  });
  root.querySelectorAll<HTMLElement>('[data-i18n-title]').forEach(el => {
    const k = el.getAttribute('data-i18n-title')!;
    if (el.dataset.i18nTitleDef === undefined) el.dataset.i18nTitleDef = el.title || '';
    el.title = t(k, undefined, el.dataset.i18nTitleDef);
  });
  root.querySelectorAll<HTMLElement>('[data-i18n-aria]').forEach(el => {
    const k = el.getAttribute('data-i18n-aria')!;
    if (el.dataset.i18nAriaDef === undefined) el.dataset.i18nAriaDef = el.getAttribute('aria-label') || '';
    el.setAttribute('aria-label', t(k, undefined, el.dataset.i18nAriaDef));
  });
}

export function setLang(l: Lang) {
  lang = l;
  try { localStorage.setItem('tesserax:lang', l); } catch (e) {}
  document.documentElement.lang = l;
  applyI18n();
  listeners.forEach(f => { try { f(); } catch (e) {} });
}

export function initI18n() {
  lang = detectLang();
  document.documentElement.lang = lang;
  applyI18n();
}
